import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TokenProviderService } from './token-provider.service';
import Coordinates from '../../model/coordinates';
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_GEOCODE_URL,
  GOOGLE_MAPS_OPTIMIZE_URL,
} from '../../../globals/globals';
import { map, switchMap } from 'rxjs/operators';
import { Observable, from } from 'rxjs';
import PlaceResult = google.maps.places.PlaceResult;
import moment from 'moment-timezone';
import { TourDetailTourItem } from 'src/app/pages/immo-ip/tour/tour-page/employee-tour-edit-modal/helper/model/tour-detail-tour-item';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import { ImmoTaskType } from 'src/app/pages/immo-ip/overview/immo-task/model/immo-task-type';
import { TourDetailCoordinates } from 'src/app/pages/immo-ip/tour/tour-page/employee-tour-edit-modal/helper/model/tour-detail-coordinates';
import DateHelper from 'src/app/helpers/date-helper';

const LOADING_SHIPMENT_LABEL = 'loading';
const PICKUP_DURATION = '300s';
const DELIVERY_DURATION = '300s';
const LOADING_DURATION = '300s';
const LOADING_PENALTY_COST = 10_000_000;

@Injectable({
  providedIn: 'root',
})
export class MapsService {
  constructor(
    private http: HttpClient,
    private tokenProvider: TokenProviderService
  ) {}

  public getGeocode(address: String): Observable<GoogleGeoCode | null> {
    return this.http
      .get<any>(
        `${GOOGLE_MAPS_GEOCODE_URL}${address}&key=${GOOGLE_MAPS_API_KEY}`
      )
      .pipe(
        map((res) => {
          const placeResult = res.results[0];
          if (placeResult) {
            return {
              coordinates: Coordinates.of(
                placeResult.geometry.location.lat,
                placeResult.geometry.location.lng
              ),
              postalCode: this.getPostalCode(placeResult),
            };
          }
          return null;
        })
      );
  }

  public optimizeRoute({
    tourItemsForMap,
    date,
    startLocation,
    endLocation = startLocation,
    loadingLocation,
    loadingTimePreference = LoadingTimePreference.MORNING,
  }: {
    tourItemsForMap: TourDetailTourItem[];
    date: Date;
    startLocation: Coordinates;
    endLocation?: Coordinates;
    loadingLocation: Coordinates;
    loadingTimePreference?: LoadingTimePreference;
  }): Observable<string[]> {
    return from(this.tokenProvider.getAccessToken()).pipe(
      switchMap((token) => {
        const vehicle = {
          startLocation: {
            latitude: startLocation.lat,
            longitude: startLocation.lng,
          },
          endLocation: {
            latitude: endLocation.lat,
            longitude: endLocation.lng,
          },
          costPerHour: 27,
        };

        const shipmentInfo = tourItemsForMap
          .map((item): ShipmentInfo => {
            if (item.subProject) {
              let type: ShipmentType = ShipmentType.DELIVERY;

              if (item.subProject.type === SUB_PROJECT_TYPES.REPO) {
                type = ShipmentType.PICKUP;
              }

              return {
                id: String(item.id),
                coordinates:
                  item.subProject.projectPlacement.address.coordinates,
                type,
              };
            }

            if (item.immoTask) {
              let type: ShipmentType = ShipmentType.DELIVERY;

              if (item.immoTask.type === ImmoTaskType.COLLECT_KEY) {
                type = ShipmentType.PICKUP;
              }

              return {
                id: String(item.id),
                coordinates: item.immoTask.address.coordinates,
                type,
              };
            }

            return undefined;
          })
          .filter((item) => typeof item !== 'undefined');

        const shipments = shipmentInfo.map((info) => {
          const pickups = [];
          const deliveries = [];

          if (info.type === ShipmentType.PICKUP) {
            pickups.push({
              arrivalWaypoint: {
                location: {
                  latLng: {
                    latitude: info.coordinates.lat,
                    longitude: info.coordinates.lng,
                  },
                },
              },
              duration: PICKUP_DURATION,
            });
          }

          if (info.type === ShipmentType.DELIVERY) {
            deliveries.push({
              arrivalWaypoint: {
                location: {
                  latLng: {
                    latitude: info.coordinates.lat,
                    longitude: info.coordinates.lng,
                  },
                },
              },
              duration: DELIVERY_DURATION,
            });
          }

          return {
            label: info.id,
            pickups,
            deliveries,
          };
        });

        const formattedDate = moment(date).format('YYYY-MM-DD');

        const loadingShipment = {
          label: LOADING_SHIPMENT_LABEL,
          pickups: [
            {
              arrivalWaypoint: {
                location: {
                  latLng: {
                    latitude: loadingLocation.lat,
                    longitude: loadingLocation.lng,
                  },
                },
              },
              duration: LOADING_DURATION,
              timeWindows:
                loadingTimePreference === LoadingTimePreference.MORNING
                  ? [
                      {
                        startTime: moment
                          .tz(
                            `${formattedDate} 07:00`,
                            DateHelper.DEFAULT_TIME_ZONE
                          )
                          .format(),
                        endTime: moment
                          .tz(
                            `${formattedDate} 08:00`,
                            DateHelper.DEFAULT_TIME_ZONE
                          )
                          .format(),
                      },
                    ]
                  : [
                      {
                        startTime: moment
                          .tz(
                            `${formattedDate} 17:00`,
                            DateHelper.DEFAULT_TIME_ZONE
                          )
                          .format(),
                        endTime: moment
                          .tz(
                            `${formattedDate} 18:00`,
                            DateHelper.DEFAULT_TIME_ZONE
                          )
                          .format(),
                      },
                    ],
            },
          ],
          deliveries: [],
          penaltyCost: LOADING_PENALTY_COST,
        };

        const body = {
          timeout: '5s',
          model: {
            shipments:
              loadingTimePreference === LoadingTimePreference.MORNING
                ? [loadingShipment, ...shipments]
                : [...shipments, loadingShipment],
            vehicles: [vehicle],
            globalStartTime: moment
              .tz(`${formattedDate} 07:00`, DateHelper.DEFAULT_TIME_ZONE)
              .format(),
            globalEndTime: moment
              .tz(`${formattedDate} 18:00`, DateHelper.DEFAULT_TIME_ZONE)
              .format(),
          },
        };

        return this.http
          .post<{ routes: { visits: { shipmentLabel: string }[] }[] }>(
            GOOGLE_MAPS_OPTIMIZE_URL,
            body,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .pipe(
            map((data) =>
              data.routes
                .flatMap((route) => route.visits)
                .map((visit) => visit.shipmentLabel)
                .filter((label) => label !== LOADING_SHIPMENT_LABEL)
            )
          );
      })
    );
  }

  private getPostalCode(placeResult: PlaceResult): string {
    let result = '';
    for (let component of placeResult.address_components) {
      let types = component.types;
      if (types.includes('postal_code')) {
        result = result + component.long_name;
      }
    }
    return result;
  }
}

export type GoogleGeoCode = {
  coordinates: Coordinates;
  postalCode: string;
};

type ShipmentInfo = {
  id: string;
  coordinates: TourDetailCoordinates;
  type: ShipmentType;
};

export enum ShipmentType {
  DELIVERY = 'delivery',
  PICKUP = 'pickup',
}

export enum LoadingTimePreference {
  MORNING = 'morning',
  EVENING = 'evening',
}
